import { TextInput, Button, TextArea } from '@cimplico/components';
import { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import EntitySelect from '../../components/entity-select/EntitySelect';
import { toastNotification } from '../../components/toast/Toast';
import { UpdateGroupFields, useUpdateGroups } from '../../queries/use-update-groups';
import { Entity } from '../../types/entity';
import { Group } from '../../types/group';
import { useIsActiveSubscription } from '../../hooks/use-is-active-subscription';

type EditGroupFormProps = {
    group: Group;
};

function EditGroupForm({ group }: EditGroupFormProps): JSX.Element {
    const navigate = useNavigate();
    const updateGroupsQuery = useUpdateGroups();
    const activeSubscription = useIsActiveSubscription();
    const groupEntities = group.entities.map((ent) => ent.id);

    const [name, setName] = useState<{ value?: string; error?: string }>({ value: group.name });
    const [description, setDescription] = useState<string | undefined>(group.description);
    const [entities, setEntities] = useState<string[]>(groupEntities);

    function handleCancel(): void {
        navigate('..');
    }

    function handleNameChange(value: string): void {
        setName({ value: value.trim(), error: undefined });
    }

    function handleDescriptionChange(value: string): void {
        setDescription(value);
    }

    function handleEntitiesChange(value: Entity[]): void {
        const entityIds = value.map((v) => v.id);
        setEntities(entityIds);
    }

    function handleSubmit(e: React.FormEvent<HTMLFormElement>): void {
        e.preventDefault();

        let error = false;

        if (!name.value) {
            setName((prev) => ({ ...prev, error: 'A name is required.' }));
            error = true;
        }
        if (error) {
            toastNotification('error', 'Unable to update group details!');
            return;
        }

        // Find any changed fields
        const fields: UpdateGroupFields = {};

        if (name.value !== group.name) {
            fields.name = name.value;
        }
        if (description !== group.description) {
            fields.description = description;
        }
        if (entities.length !== groupEntities.length || !entities.every((ent) => groupEntities.includes(ent))) {
            fields.entities = entities;
        }

        if (Object.keys(fields).length === 0) {
            handleCancel();
            return;
        }
        updateGroupsQuery.mutate([{ id: group.id, fields }], {
            onError: (e) => {
                const result = e.response?.data.results[0];
                setName((prev) => ({ ...prev, error: result.error?.fields?.name }));
                toastNotification('error', 'Unable to update group details!');
            },
            onSuccess: () => {
                handleCancel();
                toastNotification('success', 'Group details updated!');
            },
        });
    }
    return (
        <form onSubmit={handleSubmit}>
            <fieldset disabled={updateGroupsQuery.isLoading} className="flex flex-col gap-8">
                <TextInput
                    defaultValue={group.name}
                    title="Group Name"
                    errorMessage={name?.error}
                    isError={Boolean(name?.error)}
                    onChange={handleNameChange}
                    placeholder="Group name"
                    isDisabled={!activeSubscription || updateGroupsQuery.isLoading}
                />

                <TextArea
                    title="Description"
                    isOptional
                    defaultValue={group.description}
                    onChange={handleDescriptionChange}
                    placeholder="Description..."
                    characterLimit={250}
                    isDisabled={!activeSubscription || updateGroupsQuery.isLoading}
                />

                <EntitySelect
                    onChange={handleEntitiesChange}
                    title="Entities"
                    isOptional
                    isMulti
                    defaultEntityIds={groupEntities}
                    isDisabled={!activeSubscription || updateGroupsQuery.isLoading}
                />

                <div className="flex justify-end gap-4">
                    <Button type="hollow" label="Cancel" onClick={handleCancel} disabled={updateGroupsQuery.isLoading} />
                    <Button
                        type="primary"
                        label={updateGroupsQuery.isLoading ? 'Saving...' : 'Save'}
                        isSubmit
                        isLoading={updateGroupsQuery.isLoading}
                        disabled={!activeSubscription}
                    />
                </div>
            </fieldset>
        </form>
    );
}

export default EditGroupForm;
